import { Component, type ReactNode } from 'react';
import { ErrorPanel } from './ErrorPanel';

type State = { error: Error | null };

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: unknown) {
    // Hand off to whatever monitoring is wired (Sentry, console for dev).
    // We deliberately do NOT include the error message in user-visible text
    // since it can contain stack frames or PHI in dev environments.
    if (typeof window !== 'undefined' && (window as unknown as { __reportClientError?: (e: Error, i: unknown) => void }).__reportClientError) {
      (window as unknown as { __reportClientError: (e: Error, i: unknown) => void })
        .__reportClientError(error, info);
    } else {
      console.error('Unhandled error in component tree:', error, info);
    }
  }

  reset = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  render() {
    if (this.state.error) {
      return (
        <ErrorPanel
          title="The app hit an unexpected error"
          detail="The error has been recorded. You can refresh and try again. If it keeps happening, your session may have expired."
          action={{ label: 'Refresh', onClick: this.reset }}
        />
      );
    }
    return this.props.children;
  }
}
